import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
    LayoutDashboard, Package, Tags, Image as ImageIcon, Palette,
    Users, BarChart3, LogOut, ExternalLink, Menu, X
} from "lucide-react";
import { api } from "../../lib/api";
import BrandMark from "../../components/BrandMark";

const NAV = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/admin/produtos", icon: Package, label: "Produtos" },
    { to: "/admin/categorias", icon: Tags, label: "Categorias" },
    { to: "/admin/banners", icon: ImageIcon, label: "Banners" },
    { to: "/admin/visual", icon: Palette, label: "Gestão visual" },
    { to: "/admin/leads", icon: Users, label: "Leads" },
    { to: "/admin/metricas", icon: BarChart3, label: "Métricas" },
];

const AdminLayout = () => {
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");

    useEffect(() => {
        if (!localStorage.getItem("admin_token")) {
            nav("/admin/login");
            return;
        }
        api.get("/auth/me")
            .then(({ data }) => setEmail(data.email))
            .catch(() => {
                localStorage.removeItem("admin_token");
                nav("/admin/login");
            });
    }, [nav]);

    const logout = () => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_email");
        nav("/admin/login");
    };

    const SidebarLinks = ({ onClick }) => (
        <nav className="space-y-1 px-3">
            {NAV.map(({ to, icon: Icon, label, end }) => (
                <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={onClick}
                    data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors ${
                            isActive ? "bg-brand-gold/15 text-brand-gold" : "text-white/70 hover:text-white hover:bg-white/5"
                        }`
                    }
                >
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                    {label}
                </NavLink>
            ))}
        </nav>
    );

    return (
        <div data-testid="admin-layout" translate="no" className="min-h-screen bg-brand-bg flex notranslate">
            {/* Desktop sidebar */}
            <aside className="hidden md:flex w-64 bg-brand-ink text-white flex-col fixed inset-y-0 left-0 z-30">
                <div className="px-5 py-5 border-b border-white/10">
                    <Link to="/admin" className="flex items-center gap-3" data-testid="admin-sidebar-logo">
                        <BrandMark size={40} bgClass="bg-brand-gold/15 border border-brand-gold/40" />
                        <div>
                            <div className="font-heading font-bold text-sm leading-tight">Painel Admin</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold">Vitrine Deus Proverá</div>
                        </div>
                    </Link>
                </div>
                <div className="flex-1 py-4 overflow-y-auto">
                    <SidebarLinks />
                </div>
                <div className="px-3 py-4 border-t border-white/10 space-y-1">
                    <Link to="/" target="_blank" data-testid="admin-view-store" className="flex items-center gap-3 px-3 py-2 text-xs text-white/60 hover:text-white">
                        <ExternalLink className="w-3.5 h-3.5" /> Ver loja
                    </Link>
                    <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-white/60 hover:text-brand-error">
                        <LogOut className="w-3.5 h-3.5" /> Sair
                    </button>
                    {email && <div className="px-3 pt-2 text-[10px] text-white/30 truncate">{email}</div>}
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-brand-ink text-white h-14 flex items-center justify-between px-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <BrandMark size={32} bgClass="bg-brand-gold/15 border border-brand-gold/40" />
                    <div className="font-heading font-bold text-sm">Admin</div>
                </div>
                <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu" className="w-9 h-9 inline-flex items-center justify-center hover:bg-white/10 rounded-sm">
                    <Menu className="w-5 h-5" />
                </button>
            </div>
            {open && (
                <div className="md:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setOpen(false)}>
                    <aside className="absolute right-0 top-0 bottom-0 w-72 bg-brand-ink text-white py-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 mb-4">
                            <div className="text-sm font-bold">Menu</div>
                            <button onClick={() => setOpen(false)} className="w-9 h-9 inline-flex items-center justify-center hover:bg-white/10 rounded-sm"><X className="w-5 h-5" /></button>
                        </div>
                        <SidebarLinks onClick={() => setOpen(false)} />
                        <div className="px-3 mt-6 pt-4 border-t border-white/10 space-y-1">
                            <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 text-xs text-white/60 hover:text-brand-error"><LogOut className="w-3.5 h-3.5" /> Sair</button>
                        </div>
                    </aside>
                </div>
            )}

            {/* Content */}
            <main className="flex-1 min-w-0 md:ml-64 pt-14 md:pt-0">
                <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-10 max-w-7xl w-full">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
